import { useEffect, useState } from 'react'
import { billingApiService } from '../../services/billingApiService'
import { ApiError } from '../../services/BaseApiService'
import { Button, Modal } from '../ui'
import type { PlanCatalog, PlanInfo } from '../../types/billingStatement'
import { formatCents, type OverageSettingsView } from '../../utils/billingStanding'
import { colors, fontSize, radius, spacing } from '../../styles/theme'

/**
 * Current plan, and a "Change plan" comparison of every self-serve plan built from GET billing/plans (prices from
 * billing_rate, features from PlanFeatures). Choosing a plan goes through a confirmation that states what changes,
 * then the existing payment/set-plan. A caller without Manage plan sees the comparison read-only; a Free or exempt
 * team's plan is assigned and has no change action.
 */

const th: React.CSSProperties = { padding: spacing.sm, textAlign: 'center', fontSize: fontSize.sm, verticalAlign: 'top' }
const td: React.CSSProperties = { padding: `${spacing.xs} ${spacing.sm}`, fontSize: fontSize.sm, borderTop: `1px solid ${colors.border}` }

function price(p: PlanInfo): string {
  return p.monthlyMinimumCents != null ? `${formatCents(p.monthlyMinimumCents)}/mo` : '—'
}

function Comparison({ catalog, onSelect }: { catalog: PlanCatalog; onSelect?: (p: PlanInfo) => void }) {
  const showConcurrency = catalog.plans.some(p => p.concurrentRenders != null)
  const isCurrent = (p: PlanInfo) => p.code === catalog.currentPlanCode
  const col = (p: PlanInfo): React.CSSProperties => isCurrent(p) ? { background: colors.primaryLight ?? colors.background } : {}

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
        <thead>
          <tr>
            <th style={{ ...th, textAlign: 'left' }} />
            {catalog.plans.map(p => (
              <th key={p.code} style={{ ...th, ...col(p) }} data-testid={`plan-${p.code}`} data-current={isCurrent(p) || undefined}>
                <div style={{ fontSize: fontSize.base, fontWeight: 700 }}>{p.name}</div>
                {isCurrent(p) && <div style={{ fontSize: fontSize.xs, color: colors.primary, fontWeight: 600 }}>Current plan</div>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={td}>Monthly price</td>
            {catalog.plans.map(p => <td key={p.code} style={{ ...td, ...col(p), textAlign: 'center' }}>{price(p)}</td>)}
          </tr>
          <tr>
            <td style={td}>Included pages</td>
            {catalog.plans.map(p => <td key={p.code} style={{ ...td, ...col(p), textAlign: 'center' }}>{p.includedPages?.toLocaleString() ?? '—'}</td>)}
          </tr>
          <tr>
            <td style={td}>Each page beyond</td>
            {catalog.plans.map(p => <td key={p.code} style={{ ...td, ...col(p), textAlign: 'center' }}>{p.perPageCents != null ? formatCents(p.perPageCents) : '—'}</td>)}
          </tr>
          {showConcurrency && (
            <tr data-testid="concurrency-row">
              <td style={td}>Documents rendering at once</td>
              {catalog.plans.map(p => <td key={p.code} style={{ ...td, ...col(p), textAlign: 'center' }}>{p.concurrentRenders ?? '—'}</td>)}
            </tr>
          )}
          {catalog.features.map(f => (
            <tr key={f.code}>
              <td style={td}>{f.label}</td>
              {catalog.plans.map(p => {
                const has = p.features.includes(f.code)
                return (
                  <td key={p.code} style={{ ...td, ...col(p), textAlign: 'center', color: has ? colors.success : colors.textMuted }}
                    aria-label={`${f.label}: ${has ? 'included' : 'not included'} on ${p.name}`}>
                    {has ? '✓' : '—'}
                  </td>
                )
              })}
            </tr>
          ))}
          {onSelect && (
            <tr>
              <td style={td} />
              {catalog.plans.map(p => (
                <td key={p.code} style={{ ...td, ...col(p), textAlign: 'center', paddingTop: spacing.md }}>
                  {isCurrent(p)
                    ? <span style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>Current</span>
                    : <Button size="sm" variant="secondary" onClick={() => onSelect(p)}>Choose {p.name}</Button>}
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

function Confirm({ catalog, target, overage, busy, error, onBack, onConfirm }: {
  catalog: PlanCatalog; target: PlanInfo; overage?: OverageSettingsView; busy: boolean; error: string; onBack: () => void; onConfirm: () => void
}) {
  const current = catalog.plans.find(p => p.code === catalog.currentPlanCode)
  const downgrade = current != null && target.rank < current.rank
  const lost = current ? catalog.features.filter(f => current.features.includes(f.code) && !target.features.includes(f.code)) : []
  const diff = (target.monthlyMinimumCents ?? 0) - (current?.monthlyMinimumCents ?? 0)
  // During the trial the month isn't billed on any plan, so there are no extra pages to warn about.
  const over = !catalog.inTrial && target.includedPages != null && catalog.pagesThisPeriod > target.includedPages
  const extra = over ? catalog.pagesThisPeriod - target.includedPages! : 0
  // The same overage settings the month-end bill applies: off charges nothing past the allowance, a limit clamps it.
  const uncappedCents = target.perPageCents != null ? Math.round(extra * target.perPageCents) : null
  const limitDollars = overage?.overageLimitDollars ?? null
  const capped = overage?.allowOverage !== false && limitDollars != null && uncappedCents != null && uncappedCents > limitDollars * 100

  return (
    <div data-testid="plan-confirm">
      <div style={{ fontSize: fontSize.base, fontWeight: 600, marginBottom: spacing.sm }}>
        {downgrade ? 'Downgrade' : 'Upgrade'} to {target.name}
        {current && <> from {current.name}</>}
      </div>
      <ul style={{ fontSize: fontSize.sm, paddingLeft: spacing.lg, margin: 0, display: 'grid', gap: spacing.xs }}>
        <li data-testid="price-diff">
          Monthly price {formatCents(target.monthlyMinimumCents ?? 0)} — {diff === 0 ? 'no change' : `${diff > 0 ? '+' : '−'}${formatCents(Math.abs(diff))} a month`}.
          {' '}{target.includedPages?.toLocaleString()} pages included, then {target.perPageCents != null ? formatCents(target.perPageCents) : '—'} per page.
        </li>
        <li data-testid="change-rule">{catalog.changeRule}</li>
        {catalog.customPricing && (
          <li>Your team has custom pricing, which stays in place: the plan changes your features, not your rate.</li>
        )}
      </ul>

      {lost.length > 0 && (
        <div data-testid="features-lost" style={{
          marginTop: spacing.md, padding: spacing.md, borderRadius: radius.sm,
          background: colors.errorLight, border: `1px solid ${colors.errorBorder}`, fontSize: fontSize.sm,
        }}>
          <div style={{ fontWeight: 600, marginBottom: spacing.xs }}>You will lose:</div>
          <ul style={{ paddingLeft: spacing.lg, margin: 0 }}>
            {lost.map(f => (
              <li key={f.code}>{f.label}{f.downgradeNote ? ` — ${f.downgradeNote}` : ''}</li>
            ))}
          </ul>
        </div>
      )}

      {over && (
        <div data-testid="usage-warning" style={{
          marginTop: spacing.md, padding: spacing.md, borderRadius: radius.sm,
          background: colors.warningLight ?? colors.background, border: `1px solid ${colors.border}`, fontSize: fontSize.sm,
        }}>
          You've rendered {catalog.pagesThisPeriod.toLocaleString()} pages this month, more than the
          {' '}{target.includedPages!.toLocaleString()} {target.name} includes. Because the whole month bills at
          {' '}{target.name}, the {extra.toLocaleString()} extra pages
          {overage?.allowOverage === false
            ? ' are not charged while overage is off.'
            : <>
                {' '}would be charged at {target.perPageCents != null ? formatCents(target.perPageCents) : 'the overage rate'} each
                {capped
                  ? `, capped at your $${limitDollars!.toLocaleString()} monthly overage limit (${formatCents(uncappedCents!)} uncapped).`
                  : '.'}
              </>}
        </div>
      )}

      {error && <div style={{ color: colors.error, fontSize: fontSize.sm, marginTop: spacing.sm }}>{error}</div>}

      <div style={{ display: 'flex', gap: spacing.sm, justifyContent: 'flex-end', marginTop: spacing.lg }}>
        <Button variant="secondary" onClick={onBack} disabled={busy}>Back</Button>
        <Button onClick={onConfirm} loading={busy} disabled={busy}>Confirm change to {target.name}</Button>
      </div>
    </div>
  )
}

export default function PlanChange({ onChanged, overage }: { onChanged: () => void; overage?: OverageSettingsView }) {
  const [catalog, setCatalog] = useState<PlanCatalog | null>(null)
  const [open, setOpen] = useState(false)
  const [target, setTarget] = useState<PlanInfo | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function load() {
    try { setCatalog(await billingApiService.getPlans()) }
    catch { setCatalog(null) }
  }
  useEffect(() => { void load() }, [])

  if (!catalog) return null

  if (catalog.assignedPlan) {
    return <div style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>Your plan is assigned by DocMorphia.</div>
  }

  async function confirm() {
    if (!target) return
    setBusy(true); setError('')
    try {
      await billingApiService.setPlan(target.code)
      setOpen(false); setTarget(null)
      await load()
      onChanged()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change plan')
    } finally { setBusy(false) }
  }

  function close() { setOpen(false); setTarget(null); setError('') }

  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        {catalog.canChange ? 'Change plan' : 'Compare plans'}
      </Button>
      <Modal open={open} onClose={close} width={760}
        title={target ? 'Confirm plan change' : catalog.canChange ? 'Change plan' : 'Compare plans'}
        subtitle={!target && !catalog.canChange ? 'Only someone who can manage the plan can change it.' : undefined}>
        {target
          ? <Confirm catalog={catalog} target={target} overage={overage} busy={busy} error={error} onBack={() => { setTarget(null); setError('') }} onConfirm={() => void confirm()} />
          : <Comparison catalog={catalog} onSelect={catalog.canChange ? setTarget : undefined} />}
      </Modal>
    </>
  )
}
